<template>
  <form class="vl-form" @submit.prevent="onSubmit()">
    <vl-grid v-if="definition" v-vl-spacer:bottom.medium>
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-properties-list v-if="definition">
          <vl-properties-label>{{ $t('deliveryprocessdefinition.detail.id') }}</vl-properties-label>
          <vl-properties-data>{{ definition.id }}</vl-properties-data>
          <vl-properties-label>{{ $t('deliveryprocessdefinition.detail.label') }}</vl-properties-label>
          <vl-properties-data>{{ definition.label }}</vl-properties-data>
        </vl-properties-list>
      </vl-column>
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-form-column v-vl-spacer:bottom.small v-vl-spacer:top.small>
          <PbsRadioButtonField
            name="deleteEntityModels"
            :label="$t('productprocessdefinition.actions.cascadeDelete.deleteEntityModels')"
          />
        </vl-form-column>
        <vl-action-group v-vl-spacer:top.medium>
          <vl-button :mod-disabled="isSaving || !canDelete" :mod-loading="isSaving">{{
            $t('general.delete')
          }}</vl-button>
          <vl-link
            :mod-disabled="isSaving"
            :mod-loading="isSaving"
            mod-button
            mod-secondary
            target="_blank"
            :to="`/configuration/deliveryprocessdefinition/${props.id}/dependencies`"
          >
            {{ $t('deliveryprocessdefinition.detail.dependencies') }}
          </vl-link>
          <vl-button :mod-disabled="isSaving" mod-secondary @click.prevent="cancel">
            {{ $t('general.cancel') }}
          </vl-button>
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <template v-if="isLoading">
      <vl-region>
        <div v-vl-align:center>
          <vl-loader :message="$t('general.formLoading')" />
        </div>
      </vl-region>
    </template>
    <template v-if="error">
      <vl-region>
        <vl-alert icon="warning" :title="$t('general.formLoadingError')" :content="error" mod-naked mod-error />
      </vl-region>
    </template>
  </form>
</template>
<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { useDeliveryProcessDefinitionDetailStore } from '@/modules/configuration/store/delivery-process-definition/delivery-process-definition-detail.store';
import { useDeliveryProcessDefinitionListStore } from '@/modules/configuration/store/delivery-process-definition/delivery-process-definition-list.store';
import { reactive } from 'vue';
import { useForm } from 'vee-validate';
import { boolean, object } from 'yup';
import { useModalStore } from '@/modules/core/store/modal.store';
import PbsRadioButtonField from '@/components/forms/PbsRadioButtonField.vue';
import { useRoute, useRouter } from 'vue-router';
import { parseException } from '@/modules/core/store/alert.store';

const detailStore = useDeliveryProcessDefinitionDetailStore();
const listStore = useDeliveryProcessDefinitionListStore();

const router = useRouter();
const route = useRoute();

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
});

const validationSchema = object({
  deleteEntityModels: boolean(),
});

const { handleSubmit, resetForm, values } = useForm({ validationSchema });

const data = reactive({
  definition: null,
  isLoading: false,
  isSaving: false,
  canDelete: false,
  error: null,
});

onMounted(async () => {
  try {
    data.isLoading = true;
    await loadDeliveryProcessDefinition();
  } catch (error) {
    data.error = parseException(error);
  } finally {
    data.isLoading = false;
  }

  setInitialFormValues();
});

const setInitialFormValues = () => {
  resetForm({
    values: {
      deleteEntityModels: true,
    },
  });
};

const loadDeliveryProcessDefinition = async () => {
  const definition = await detailStore.getDetail(props.id);
  if (definition) {
    data.canDelete = await detailStore.validateCascadeDelete(props.id);
    data.definition = definition;
  }
};

const definition = computed(() => {
  return data.definition;
});

const isLoading = computed(() => {
  return data.isLoading;
});

const isSaving = computed(() => {
  return data.isSaving;
});

const canDelete = computed(() => {
  return data.canDelete;
});

const error = computed(() => {
  return data.error;
});

const onSuccess = async () => {
  const isSuccess = await cascadeDelete();
  if (isSuccess) {
    listStore.getDefinitions();

    if (route.name == 'deliveryprocessdefinition.detail') {
      router.push({ name: 'configuration', hash: '#deliveryprocessdefinition' });
    }
  }

  closeModal();
};

const cascadeDelete = async (): Promise<boolean> => {
  if (data.isSaving) {
    return;
  }

  let isSuccess = false;
  try {
    data.isSaving = true;
    isSuccess = await detailStore.cascadeDelete(props.id, {
      deleteEntityModels: values.deleteEntityModels,
    });
  } catch (e) {
    console.error(e);
  } finally {
    data.isSaving = false;
  }
  return isSuccess;
};

const cancel = () => {
  closeModal();
};

const closeModal = () => {
  useModalStore().hideModal();
};

const onInvalidSubmit = ({ values, errors, results }) => {
  // console.log(values); // current form values
  // console.log(errors); // a map of field names and their first error message
  // console.log(results); // a detailed map of field names and their validation results
};

const onSubmit = handleSubmit(onSuccess, onInvalidSubmit);
</script>
<style lang="scss" scoped></style>
